import React from "react";
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Package, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";

export const ReturnPolicy: React.FC = () => {
  return (
    <div className='max-w-4xl mx-auto px-6 py-12'>
      {/* Page Header */}
      <div className='mb-10 text-center'>
        <div className='inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4'>
          <RefreshCw className='h-8 w-8 text-blue-600' />
        </div>
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Return & Refund Policy</h1>
        <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
          Our hassle-free return process and refund guidelines to ensure your shopping experience is
          as smooth as possible.
        </p>
      </div>

      {/* Introduction */}
      <Card className='p-6 border-0 shadow-sm mb-8'>
        <p className='text-gray-700'>
          At Stedshop LTD, we are committed to your satisfaction. However, please note that we
          operate strictly as a reseller of finished clothing items — including branded products
          (e.g., Nike, etc.) and items sourced from verified local vendors — and{" "}
          <strong>do not manufacture, tailor, or customize any garments</strong>.
        </p>
      </Card>

      {/* Eligibility for Return */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='eligibility'>
        <div className='flex items-start mb-4'>
          <CheckCircle className='h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Eligibility for Return</h2>
          </div>
        </div>

        <p className='text-gray-700 mb-4'>
          You may request a return within <strong>7 (seven) working days</strong> from the date of
          delivery, provided that:
        </p>

        <ol className='list-decimal pl-6 space-y-3 text-gray-700'>
          <li>
            The item is in its original, unworn condition with no signs of use, washing, or damage;
          </li>
          <li>
            All original tags, labels, and packaging (including the manufacturer's or vendor's cloth
            label) remain intact and attached;
          </li>
          <li>
            The item is returned in the same condition as received, with all accompanying
            accessories (if any).
          </li>
        </ol>

        <div className='mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200'>
          <p className='text-gray-700'>
            <strong>Important:</strong> Items that do not meet these conditions may be rejected, and
            no refund or exchange will be processed.
          </p>
        </div>
      </Card>

      {/* Return Process */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='process'>
        <div className='flex items-start mb-4'>
          <Package className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Return Process</h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2 font-bold'>1.</span>
            <span>
              To initiate a return, contact our customer support team at{" "}
              <strong>support@stedshop.com.ng</strong> with your order number and reason for return.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2 font-bold'>2.</span>
            <span>Upon approval, you will receive a return authorization and instructions.</span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2 font-bold'>3.</span>
            <span>
              You are responsible for the cost and safe return of the item to our designated
              warehouse unless the return is due to our error (e.g., wrong item shipped or defective
              on arrival).
            </span>
          </li>
        </ul>
      </Card>

      {/* Refund Processing */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='refund'>
        <div className='flex items-start mb-4'>
          <Clock className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Refund Processing</h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              Once we receive and inspect the returned item and confirm it meets the above
              conditions, we will process your refund.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              Refunds will be issued to the original payment method within{" "}
              <strong>7–14 working days</strong>.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              <strong>Please note:</strong> Original shipping fees are non-refundable, unless the
              return results from our error.
            </span>
          </li>
        </ul>
      </Card>

      {/* Non-Returnable Items */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-red-50' id='non-returnable'>
        <div className='flex items-start mb-4'>
          <XCircle className='h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Non-Returnable Items</h2>
          </div>
        </div>

        <p className='text-gray-700 mb-4'>
          The following items are <strong>not eligible for return</strong>:
        </p>

        <ul className='space-y-2 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-red-600 mr-2'>✗</span>
            <span>Items returned after 7 working days from delivery;</span>
          </li>
          <li className='flex items-start'>
            <span className='text-red-600 mr-2'>✗</span>
            <span>Items with removed, damaged, or altered labels;</span>
          </li>
          <li className='flex items-start'>
            <span className='text-red-600 mr-2'>✗</span>
            <span>Items that show signs of wear, odor, stains, or damage;</span>
          </li>
          <li className='flex items-start'>
            <span className='text-red-600 mr-2'>✗</span>
            <span>
              Items purchased during final sale or clearance promotions (unless defective).
            </span>
          </li>
        </ul>
      </Card>

      {/* Clarification of Business Model */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-blue-50' id='business-model'>
        <div className='flex items-start mb-4'>
          <AlertTriangle className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Clarification of Business Model</h2>
          </div>
        </div>

        <p className='text-gray-700'>
          Stedshop LTD acts solely as a <strong>reseller</strong> and does not engage in garment
          manufacturing, tailoring, or customization. All products are pre-made by brands (e.g.,
          Nike) or local vendors and sold as-is. Therefore, our returns policy reflects standard
          retail practices for finished goods — not bespoke or made-to-order items.
        </p>
      </Card>

      {/* Defective or Wrong Items */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-green-50' id='defective'>
        <div className='flex items-start mb-4'>
          <CheckCircle className='h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>Defective or Wrong Items</h2>
          </div>
        </div>

        <p className='text-gray-700'>
          If you receive a <strong>damaged, defective, or incorrect item</strong>, please notify us
          within <strong>48 hours of delivery</strong>. We will arrange a free return and full
          refund or replacement at our cost.
        </p>
      </Card>

      {/* Contact Section */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-gray-50'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Need Help with a Return?</h2>
        <p className='text-gray-700 mb-2'>Contact our customer support team:</p>
        <p className='text-gray-700 font-semibold'>
          Email: <span className='text-blue-600'>support@stedshop.com.ng</span>
        </p>
        <p className='text-gray-700 mt-4'>
          We're here to make your return process as smooth as possible!
        </p>
      </Card>
    </div>
  );
};
